(() => {
  const widgetSelector = '[data-eun-cawa]';
  const formSelector = 'form[data-type="add-to-cart-form"]';

  const getSettings = () => window.MinimogSettings || {};
  const getRoot = () => window.Shopify?.routes?.root || '/';

  function updateSelectedCount(widget) {
    const selected = widget.querySelectorAll('[data-eun-cawa-variant]:checked').length;
    const output = widget.querySelector('[data-eun-cawa-selected]');
    if (output) output.textContent = `${selected} Selected`;
  }

  function initWidget(widget) {
    if (widget.dataset.eunCawaReady === 'true') return;
    widget.dataset.eunCawaReady = 'true';

    const toggle = widget.querySelector('[data-eun-cawa-toggle]');
    const regularItems = [...widget.querySelectorAll('[data-eun-cawa-regular]')];
    const maxVisible = Number.parseInt(widget.dataset.maxVisible || '3', 10);

    widget.addEventListener('change', event => {
      if (event.target.matches('[data-eun-cawa-variant]')) updateSelectedCount(widget);
    });

    if (toggle) {
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        regularItems.slice(maxVisible).forEach(item => {
          item.hidden = expanded;
        });
        toggle.setAttribute('aria-expanded', String(!expanded));
        toggle.textContent = expanded
          ? `View ${Math.max(regularItems.length - maxVisible, 0)} more`
          : 'Show fewer';
      });
      toggle.setAttribute('aria-expanded', 'false');
    }

    updateSelectedCount(widget);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(widgetSelector).forEach(initWidget);
  }

  function getWidgetForForm(form) {
    const productId = form.dataset.productId;
    if (!productId) return null;
    return [...document.querySelectorAll(widgetSelector)].find(widget => widget.dataset.productId === productId) || null;
  }

  function setMessage(widget, message) {
    const output = widget.querySelector('[data-eun-cawa-message]');
    if (!output) return;
    output.textContent = message || '';
    output.hidden = !message;
  }

  function setLoading(form, loading) {
    const wrapper = form.closest('.product-form');
    const button = form.querySelector('[name="add"]');
    wrapper?.classList.toggle('adding', loading);
    if (button) {
      if (loading) {
        button.dataset.eunCawaWasDisabled = String(button.disabled);
        button.disabled = true;
      } else {
        button.disabled = button.dataset.eunCawaWasDisabled === 'true';
        delete button.dataset.eunCawaWasDisabled;
      }
      button.setAttribute('aria-busy', String(loading));
    }
  }

  function getMainItem(form) {
    const formData = new FormData(form);
    const id = Number.parseInt(String(formData.get('id') || ''), 10);
    const quantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1);
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (!match || value instanceof File || value === '') continue;
      properties[match[1]] = value;
    }

    const item = { id, quantity };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number.parseInt(String(sellingPlan), 10);
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  async function getCart() {
    const response = await fetch(`${getRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to read the cart. Please try again.');
    return response.json();
  }

  function isPromotionalGift(item) {
    return String(item.properties?._free_gift || '').toLowerCase() === 'true';
  }

  function getSelectedGiftVariantIds(widget) {
    return [...widget.querySelectorAll('[data-eun-cawa-gift-input]:checked')]
      .map(input => String(input.dataset.variantId || ''))
      .filter(Boolean);
  }

  function isGiftForProduct(item, qualifyingProductId) {
    if (!isPromotionalGift(item)) return false;
    const itemQualifyingProductId = String(item.properties?._qualifying_product_id || '');
    return !itemQualifyingProductId || itemQualifyingProductId === String(qualifyingProductId || '');
  }

  async function syncExistingGiftChoices(cart, selectedGiftVariantIds, qualifyingProductId) {
    if (!selectedGiftVariantIds.length) return cart;

    const updates = {};
    const selectedIds = new Set(selectedGiftVariantIds.map(String));

    (cart.items || []).forEach(item => {
      if (!isGiftForProduct(item, qualifyingProductId) || !item.key) return;

      if (selectedIds.has(String(item.variant_id))) {
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
        return;
      }

      updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return cart;

    const settings = getSettings();
    const response = await fetch(settings.routes?.cart_update_url || `${getRoot()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to update your free gifts.');
    }
    return result;
  }

  function getSelectedItems(widget, cart) {
    const items = [];
    const seen = new Set();
    const qualifyingProductId = String(widget.dataset.productId || '');
    const promotionalGiftVariantIds = new Set(
      (cart.items || [])
        .filter(item => isGiftForProduct(item, qualifyingProductId))
        .map(item => String(item.variant_id))
    );

    widget.querySelectorAll('[data-eun-cawa-variant]:checked').forEach(input => {
      const id = String(input.dataset.variantId || '');
      if (!id || seen.has(id)) return;

      const isGift = input.hasAttribute('data-eun-cawa-gift-input');
      if (isGift && promotionalGiftVariantIds.has(id)) return;

      const item = { id: Number.parseInt(id, 10), quantity: 1 };
      if (isGift) {
        item.properties = {
          _free_gift: 'true',
          _qualifying_product_id: widget.dataset.productId
        };
      }

      seen.add(id);
      items.push(item);
    });

    return items;
  }

  async function addItems(items, cartDrawer) {
    const settings = getSettings();
    const body = { items };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map(section => section.id);
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(settings.routes?.cart_add_url || `${getRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add these items to the cart.');
    }
    return result;
  }

  function showSuccess(result, cartDrawer) {
    const settings = getSettings();
    const strings = window.MinimogStrings || {};

    if (cartDrawer && settings.enable_cart_drawer && result.sections) {
      cartDrawer.renderContents(result);
      const drawerItems = cartDrawer.querySelector('m-cart-drawer-items');
      if (drawerItems && window.MinimogTheme?.Notification) {
        window.MinimogTheme.Notification.show({
          target: drawerItems,
          method: 'prepend',
          type: 'success',
          message: strings.itemAdded || 'Items added to cart',
          delay: 400
        });
      }
    } else {
      window.location.assign(settings.routes?.cart || `${getRoot()}cart`);
      return;
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, result);
    }
  }

  async function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(formSelector)) return;

    const widget = getWidgetForForm(form);
    if (!widget) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (widget.dataset.eunCawaSubmitting === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setMessage(widget, '');
    setLoading(form, true);
    widget.dataset.eunCawaSubmitting = 'true';

    try {
      const mainItem = getMainItem(form);
      if (!Number.isFinite(mainItem.id)) throw new Error('Please select a product option before adding to cart.');

      let cart = await getCart();
      const selectedGiftVariantIds = getSelectedGiftVariantIds(widget);
      if (selectedGiftVariantIds.length) {
        cart = await syncExistingGiftChoices(cart, selectedGiftVariantIds, widget.dataset.productId);
      }
      const selectedItems = getSelectedItems(widget, cart);
      const result = await addItems([mainItem, ...selectedItems], document.querySelector('m-cart-drawer'));
      showSuccess(result, document.querySelector('m-cart-drawer'));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add these items to the cart.';
      setMessage(widget, message);
      const errorWrapper = form.querySelector('.prod__form-error') || form.closest('product-form')?.querySelector('.prod__form-error');
      if (errorWrapper && window.MinimogTheme?.Notification) {
        window.MinimogTheme.Notification.show({
          target: errorWrapper,
          method: 'appendChild',
          type: 'warning',
          message,
          last: 3000
        });
      }
    } finally {
      widget.dataset.eunCawaSubmitting = 'false';
      setLoading(form, false);
    }
  }

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', event => initAll(event.target));

  if (document.readyState !== 'loading') initAll();
})();
